import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useOutletContext, useParams } from 'react-router';
import type { MembershipSummary } from '../../../src/shared/auth';
import type { JobStatus, Job, JobFailure } from '../../../src/shared/jobs';
import { jobsKeys, fetchJobs, retryJob } from '../../api/jobs';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingScreen } from '../../components/LoadingScreen';

interface StatusOption {
  label: string;
  value: JobStatus | null;
}

const STATUS_OPTIONS: StatusOption[] = [
  { label: 'All', value: null },
  { label: 'Queued', value: 'queued' },
  { label: 'Processing', value: 'processing' },
  { label: 'Succeeded', value: 'succeeded' },
  { label: 'Failed', value: 'failed' },
  { label: 'Dead', value: 'dead' },
];

function formatTimestamp(epoch: number): string {
  return new Date(epoch * 1000).toLocaleString();
}

function JobCard({
  job,
  failures,
  onRetry,
  isRetrying,
}: {
  job: Job;
  failures: JobFailure[];
  onRetry: (jobId: string) => void;
  isRetrying: boolean;
}): React.JSX.Element {
  const jobFailures = failures.filter((f) => f.jobId === job.id);
  const isFailed = job.status === 'failed' || job.status === 'dead';

  return (
    <div
      className={`rounded-lg border bg-white p-4 shadow-sm ${
        isFailed ? 'border-red-200' : 'border-slate-200'
      }`}
      data-testid={`job-card-${job.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span
              data-testid={`job-status-${job.id}`}
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                job.status === 'succeeded'
                  ? 'bg-green-100 text-green-700'
                  : job.status === 'failed'
                    ? 'bg-red-100 text-red-700'
                    : job.status === 'dead'
                      ? 'bg-red-200 text-red-800'
                      : job.status === 'processing'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-slate-100 text-slate-700'
              }`}
            >
              {job.status}
            </span>
            <span className="truncate text-sm font-medium text-slate-900">{job.type}</span>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Attempts: {job.attempts}/{job.maxAttempts} &middot; Updated:{' '}
            {formatTimestamp(job.updatedAt)}
          </p>
          {job.lastError !== null && (
            <p className="mt-2 text-sm text-red-600" data-testid={`job-error-${job.id}`}>
              {job.lastError}
            </p>
          )}
          {jobFailures.map((failure) => (
            <div
              key={failure.id}
              className="mt-2 rounded border border-red-100 bg-red-50 px-3 py-2"
              data-testid={`job-failure-${failure.id}`}
            >
              <p className="text-sm font-medium text-red-700">Failure</p>
              <p className="mt-0.5 text-sm text-red-600">{failure.error}</p>
              <p className="mt-0.5 text-xs text-red-500">
                Failed at: {formatTimestamp(failure.failedAt)}
                {failure.retriedAt !== null && (
                  <> &middot; Retried at: {formatTimestamp(failure.retriedAt)}</>
                )}
              </p>
            </div>
          ))}
        </div>
        {isFailed && (
          <button
            type="button"
            onClick={() => onRetry(job.id)}
            disabled={isRetrying}
            className="shrink-0 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid={`retry-btn-${job.id}`}
          >
            {isRetrying ? 'Retrying…' : 'Retry'}
          </button>
        )}
      </div>
    </div>
  );
}

export function JobsPage(): React.JSX.Element {
  const { slug } = useParams<'slug'>();
  const { membership } = useOutletContext<{ membership: MembershipSummary }>();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<JobStatus | null>(null);

  const jobsQuery = useQuery({
    queryKey: jobsKeys.list(slug!, statusFilter ?? undefined),
    queryFn: ({ signal }) => fetchJobs(slug!, statusFilter ?? undefined, signal),
    enabled: slug !== undefined,
  });

  const retryMutation = useMutation({
    mutationFn: (jobId: string) => retryJob(slug!, jobId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobsKeys.all(slug!) });
    },
  });

  if (slug === undefined) {
    return <p className="text-sm text-slate-500">Workspace slug not found.</p>;
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight">Background Jobs</h1>
      <p className="mt-1 text-sm text-slate-500">
        {membership.name} &middot; Failed jobs are retryable
      </p>

      <div className="mt-6 flex flex-wrap gap-2" role="group" aria-label="Status filter">
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.label}
            type="button"
            onClick={() => setStatusFilter(opt.value)}
            className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
              statusFilter === opt.value
                ? 'border-slate-900 bg-slate-900 text-white'
                : 'border-slate-300 text-slate-700 hover:bg-slate-100'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {retryMutation.isError && (
        <div className="mt-4">
          <ErrorNotice error={retryMutation.error} />
        </div>
      )}

      <div className="mt-4 space-y-3">
        {jobsQuery.isPending && <LoadingScreen />}

        {jobsQuery.isError && <ErrorNotice error={jobsQuery.error} />}

        {jobsQuery.isSuccess && jobsQuery.data.items.length === 0 && (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
            <p className="text-sm text-slate-500" data-testid="jobs-empty">
              {statusFilter !== null ? `No ${statusFilter} jobs found.` : 'No background jobs yet.'}
            </p>
          </div>
        )}

        {jobsQuery.isSuccess &&
          jobsQuery.data.items.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              failures={jobsQuery.data.failures}
              onRetry={(jobId) => retryMutation.mutate(jobId)}
              isRetrying={retryMutation.isPending}
            />
          ))}
      </div>
    </div>
  );
}
